import React, {useState} from 'react'
import Select, {components} from 'react-select'
import {ToolTipUI} from '../../widgets/components/UI/ToolTipUI'
import {ToolTipUIForModal} from '../../widgets/components/UI/ToolTipUIForModal'
import {Field, ErrorMessage, getIn, FieldProps} from 'formik'
import {useThemeMode} from '../../../../_metronic/partials'
import Skeleton from 'react-loading-skeleton'

interface SelectSearchInputProps {
  fieldName: string
  label?: string
  toolTipText?: string
  placeholder: string
  formik: any
  options: any[]
  margin?: string
  width?: number
  isStarRequired?: boolean
  isTooltipForModal?: boolean
  isTooltipNotRequired?: boolean
  isMulti?: boolean
  disabled?: boolean
  loading?: boolean // New loading prop for skeleton
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  ariaLabel?: string
  buttonLabel?: string // Add a prop for button label
  onButtonClick?: () => void // Add a prop for button click handler
  onChange?: (e: any) => void
  isPopulateData?: boolean
  noDataLabel?: string
  isButtonHidden?: boolean
  isMarginNotRequired?: boolean
}

// Custom Menu List to add the button at the end of the dropdown and show options
const CustomMenuList = (props: any) => {
  const {buttonLabel, onButtonClick, loading, noDataLabel, isButtonHidden} = props // Extract buttonLabel, onButtonClick, and loading from props

  return (
    <div className='custom-menu-list' style={{position: 'relative'}}>
      <components.MenuList
        {...props}
        style={{
          maxHeight: '280px', // Set a fixed height for the dropdown list
          overflowY: 'scroll', // Enable vertical scrolling
        }}
      >
        <div style={{paddingBottom: isButtonHidden ? 0 : '65px'}}>
          {loading ? (
            <div className='p-2'>
              <Skeleton
                height={40}
                count={5}
                baseColor='var(--kt-skeleton-bg-color)'
                highlightColor='var(--kt-skeleton-highlight-bg-color)'
              />
            </div>
          ) : props.options.length === 0 ? (
            <div className='p-2 d-flex justify-content-center align-items-center'>
              {noDataLabel ?? 'No Data Found...'}
            </div>
          ) : (
            props?.children?.map((child: any, index: number) => <div key={index}>{child}</div>)
          )}
        </div>
      </components.MenuList>
      <div
        className={`sticky-button ${isButtonHidden ? 'd-none' : ''}`}
        style={{
          position: 'absolute', // Stick the button to the bottom of the list
          bottom: 0,
          width: '100%',
          borderTop: '1px solid #e5e5e5',
          backgroundColor: 'white',
          padding: '8px',
          zIndex: 1,
        }}
      >
        <button type='button' className='btn btn-primary w-100' onClick={onButtonClick}>
          {buttonLabel}
        </button>
      </div>
    </div>
  )
}

// Main component
export const SecurityClassDropdown: React.FC<SelectSearchInputProps> = ({
  fieldName,
  label,
  toolTipText,
  placeholder,
  formik,
  options,
  margin,
  width,
  isStarRequired,
  isTooltipForModal,
  isTooltipNotRequired,
  isMulti,
  disabled,
  loading, // Accept the loading prop
  onFocus,
  onBlur,
  ariaLabel,
  buttonLabel,
  onButtonClick,
  isPopulateData,
  noDataLabel,
  isButtonHidden,
  isMarginNotRequired,
}) => {
  const [, setSelectedOptions] = useState<any>(null)
  const {mode} = useThemeMode()

  const handleChange = async(selectedOptions: any) => {
    setSelectedOptions(selectedOptions)
    formik.setFieldValue(fieldName, selectedOptions)


    if (fieldName === 'shareClassId' && isPopulateData) {
      formik.setFieldValue('pricePerShare', selectedOptions?.pricePerShare ?? null)
      formik.setFieldValue('votePerShare', selectedOptions?.votePerShare ?? 0)
      formik.setFieldValue('issueDate', selectedOptions?.firstClosingDate ?? null)
      formik.setFieldValue('boardApprovalDate', selectedOptions?.firstClosingDate ?? null)
      formik.setFieldValue('dateOfIssue', selectedOptions?.firstClosingDate ?? null)
      formik.setFieldValue('dateOfBuyback', selectedOptions?.firstClosingDate ?? null)
    }
    if (fieldName === 'convertibleClassId' && isPopulateData) {
      formik.setFieldValue('dateOfIssue', selectedOptions?.dateOfIssue ?? null)
    }
    if (fieldName === 'warrantClassId' && isPopulateData) {
      formik.setFieldValue('expiryDate', selectedOptions?.expiryDate ?? null)
      formik.setFieldValue('dateOfIssue', selectedOptions?.boardApprovalDate ?? null)
      await formik.validateForm()
    }
    if (fieldName === 'shareTransactionId' && isPopulateData) {
      formik.setFieldValue('pricePerShare', selectedOptions?.pricePerShare ?? null)
      formik.setFieldValue('numberOfShares', selectedOptions?.numberOfShares ?? null)
    }
    if (fieldName === 'shareTransactionId' && isPopulateData) {
      console.log('shareTransactionId', selectedOptions)

      formik.setFieldValue('transferDate', selectedOptions?.dateOfIssue ?? null)
    }
  }

  const fieldValue = getIn(formik.values, fieldName)

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      boxShadow: 'none',
      fontSize: '12px',
      fontWeight: '500',
      borderRadius: '0.5rem',
      border: `${
        formik.touched[fieldName] && formik.errors[fieldName]
          ? '1px solid #f1416c'
          : formik.touched[fieldName] && !formik.errors[fieldName]
          ? '1px solid #50cd89'
          : mode === 'dark'
          ? 'none'
          : '1px solid #ddd'
      }`,
      '&:hover': {
        border: `${
          formik.touched[fieldName] && formik.errors[fieldName]
            ? '1px solid #f1416c'
            : formik.touched[fieldName] && !formik.errors[fieldName]
            ? '1px solid #50cd89'
            : mode === 'dark'
            ? 'none'
            : '1px solid #ddd'
        }`,
      },
      background: `${
        formik.touched[fieldName] && formik.errors[fieldName]
          ? 'rgba(241, 65, 108, 0.2)'
          : formik.touched[fieldName] && !formik.errors[fieldName]
          ? 'white'
          : mode === 'dark'
          ? '#181c32'
          : disabled
          ? '#eff2f5'
          : 'white'
      }`,
    }),
    placeholder: (provided: any) => ({
      ...provided,
      fontSize: '12px',
      color: '#a1a5b7',
    }),
  }

  return (
    <div
      className={`fv-row ${isMarginNotRequired ? '' : 'mb-8'}  ${margin ? margin : ''} ${
        width ? `col-md-${width}` : ''
      }`}
    >
      {label && (
        <label className='d-flex form-label font-size-12 text-dark text-capitalize ms-3 ms-md-0'>
          {label}
          {!isTooltipNotRequired && (
            <>
              {!isStarRequired && '*'}
              {isTooltipForModal ? (
                <ToolTipUIForModal tooltipText={toolTipText} />
              ) : (
                <ToolTipUI tooltipText={toolTipText} />
              )}
            </>
          )}
        </label>
      )}

      <Field name={fieldName} onBlur={onBlur}>
        {({field}: FieldProps) => (
          <Select
            options={options}
            onChange={handleChange}
            placeholder={placeholder}
            styles={customStyles}
            isMulti={isMulti}
            value={fieldValue}
            isDisabled={disabled}
            onBlur={onBlur}
            onFocus={onFocus}
            aria-label={ariaLabel}
            components={{
              MenuList: (props) => (
                <CustomMenuList
                  {...props}
                  noDataLabel={noDataLabel}
                  buttonLabel={buttonLabel}
                  onButtonClick={onButtonClick}
                  loading={loading} // Pass loading state to CustomMenuList
                  isButtonHidden={isButtonHidden}
                />
              ),
            }} // Pass buttonLabel, onButtonClick, and loading through the components prop
          />
        )}
      </Field>
      <div className='text-danger mt-2'>
        <ErrorMessage name={fieldName} />
      </div>
    </div>
  )
}
